import clsx from 'clsx'
import { motion, AnimatePresence } from 'motion/react'
import { useState, useEffect } from 'react'

import { withPublicImageUrl } from '@lib/utils/cdn'

import { Text } from '@components/ui-core/text/text'

import { useStep } from '../../hooks/use-step'
import { Step } from '../../utils/steps'

import {
  CONTEXT_MENU_HIDE_ANIMATION_DURATION,
  SEND_BUTTON_CLICK_DURATION,
  MESSAGE_VISIBLE_ANIMATION_DURATION,
  THINKING_STAY_VISIBLE_DURATION,
  RESPONSE_VISIBLE_ANIMATION_DURATION,
  RESPONSE_STAY_VISIBLE_DURATION,
  CHAT_INPUT_HEIGHT_ANIMATION_DURATION,
  PLUS_BUTTON_CLICK_DURATION,
  Stage,
} from './constants'

import style from './chat-input.module.scss'

export const ChatInput = ({
  stage,
  setStage,
}: {
  stage: Stage
  setStage: (stage: Stage) => void
}) => {
  const { step } = useStep()
  const [typing, setTyping] = useState(false)
  const [showPill, setShowPill] = useState(true)

  const addedSourceVisibleAnimationDuration = 0.3
  const addedSourceDelay = CONTEXT_MENU_HIDE_ANIMATION_DURATION
  const typingAnimationDuration = 1.5
  const resetDelay =
    SEND_BUTTON_CLICK_DURATION +
    MESSAGE_VISIBLE_ANIMATION_DURATION +
    THINKING_STAY_VISIBLE_DURATION +
    RESPONSE_VISIBLE_ANIMATION_DURATION +
    RESPONSE_STAY_VISIBLE_DURATION
  const typingVisibleDelay =
    CONTEXT_MENU_HIDE_ANIMATION_DURATION + addedSourceVisibleAnimationDuration

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    switch (stage) {
      case Stage.SOURCES_ADDED: {
        timeoutId = setTimeout(() => {
          setTyping(true)

          timeoutId = setTimeout(() => {
            setStage(Stage.SEND)
          }, typingAnimationDuration * 1000)
        }, typingVisibleDelay * 1000)

        break
      }
      case Stage.SEND: {
        timeoutId = setTimeout(() => {
          setTyping(false)
          setShowPill(false)

          timeoutId = setTimeout(() => {
            setStage(Stage.RESET)
          }, resetDelay * 1000)
        }, SEND_BUTTON_CLICK_DURATION * 1000)

        break
      }
      case Stage.START: {
        setShowPill(true)
        setTyping(false)

        break
      }
      // No default
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [stage, setStage, typingVisibleDelay, resetDelay])

  const showAddedSource = stage === Stage.SOURCES_ADDED || stage === Stage.SEND

  return (
    <motion.div
      initial={{ opacity: 0, height: '13.9rem' }}
      animate={{
        opacity: stage === Stage.RESET || step !== Step.AgentsGo ? 0 : 1,
        height: showPill ? '13.9rem' : '9.4rem',
      }}
      transition={{
        duration: CHAT_INPUT_HEIGHT_ANIMATION_DURATION,
      }}
      className={style.chatInput}
      style={
        {
          '--typing-duration': `${typingAnimationDuration}s`,
          '--send-button-click-duration': `${SEND_BUTTON_CLICK_DURATION}s`,
          '--plus-button-click-duration': `${PLUS_BUTTON_CLICK_DURATION}s`,
          height: showPill ? '13.9rem' : '9.4rem',
        } as React.CSSProperties
      }
    >
      <AnimatePresence>
        {showPill && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: CHAT_INPUT_HEIGHT_ANIMATION_DURATION }}
            className={clsx(style.pill, style.img)}
            style={{
              backgroundImage: `url(${withPublicImageUrl('superhuman-go-intro-go-pill.svg')})`,
            }}
          />
        )}
      </AnimatePresence>
      <div className={clsx(style.inputLine, typing && style.typing)}>
        <Text className={style.input} as="span">
          {typing ? 'Add a quick summary from Jira to my email.' : ''}
        </Text>
        {typing && <span className={style.cursor} />}
      </div>
      <div className={style.buttonLine}>
        <div
          className={clsx(style.plus, stage === Stage.ADD_SOURCE && style.clicked)}
          style={{
            backgroundImage: `url(${withPublicImageUrl('superhuman-go-intro-go-plus.svg')})`,
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: showAddedSource ? 1 : 0,
            scale: showAddedSource || stage === Stage.RESET ? 1 : 0,
          }}
          transition={{
            duration: addedSourceVisibleAnimationDuration,
            delay: addedSourceDelay,
            scale: { delay: addedSourceDelay, type: 'spring', stiffness: 300, damping: 20 },
          }}
          className={clsx(style.source, style.img)}
          style={{
            backgroundImage: `url(${withPublicImageUrl('superhuman-go-intro-go-source-added-jira.svg')})`,
          }}
        />
        <div className={clsx(style.send, stage === Stage.SEND && style.clicked)}>
          <div
            className={clsx(style.sendIcon, style.img)}
            style={{
              backgroundImage: `url(${withPublicImageUrl('superhuman-go-intro-go-send.svg')})`,
            }}
          />
        </div>
      </div>
    </motion.div>
  )
}
